import React from "react";
import { Loader2 } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { ScreenSelector } from "@/components/site/calculator/ScreenSelector";
import { ProductTypeSelector } from "@/components/site/calculator/ProductTypeSelector";
import { QuantityControl } from "@/components/site/calculator/QuantityControl";
import { DiscountField } from "@/components/site/calculator/DiscountField";
import { VolumeTable } from "@/components/site/calculator/VolumeTable";
import { OrderForm } from "@/components/site/calculator/OrderForm";
import { QuotePanel } from "@/components/site/calculator/QuotePanel";
import { useCalculator } from "@/hooks/useCalculator";
import { CALCULATOR } from "@/data/content";

export default function Calculator() {
  const calc = useCalculator();
  const { pricing } = calc;

  return (
    <div data-testid="calculator-page" className="bg-white">
      <Navbar />

      <section data-text-bump className="relative overflow-hidden bg-ink pt-28 pb-14 sm:pt-32">
        <div className="pointer-events-none absolute -right-20 top-10 h-72 w-72 rounded-full bg-brand-500/10 blur-3xl" />
        <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-brand-400">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
            {CALCULATOR.eyebrow}
          </span>
          <h1 className="mt-4 max-w-3xl font-display text-[clamp(2rem,4.5vw,3.4rem)] font-bold leading-[1.05] tracking-tight text-white">
            Υπολόγισε το <span className="text-brand-500">κόστος</span> της παραγγελίας σου
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-white/70">{CALCULATOR.subtitle}</p>
        </div>
      </section>

      <section data-text-bump className="py-12 sm:py-16">
        <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
          {!pricing ? (
            <div className="flex items-center justify-center py-24 text-black/50">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Φόρτωση…
            </div>
          ) : (
            <div className="grid gap-8 lg:grid-cols-12">
              <div className="lg:col-span-6">
                <div className="rounded-3xl border border-black/10 bg-white p-6 shadow-xl sm:p-8">
                  <h2 className="flex items-center gap-3 font-display text-xl font-bold text-ink">
                    <span className="h-5 w-1 rounded-full bg-brand-500" />
                    Τεχνικές Προδιαγραφές
                  </h2>
                  <ProductTypeSelector value={calc.product} onChange={calc.setProduct} />
                  <ScreenSelector screens={pricing.screens} value={calc.screen} onChange={calc.setScreen} />
                  <QuantityControl
                    pricing={pricing}
                    value={calc.qty}
                    onChange={calc.setQuantity}
                    screen={calc.screen}
                    priceActive={calc.priceActive}
                  />
                  <DiscountField
                    code={calc.code}
                    setCode={calc.setCode}
                    onApply={calc.applyCode}
                    checking={calc.checkingCode}
                    discount={calc.discount}
                  />
                  <VolumeTable pricing={pricing} screen={calc.screen} />
                </div>

                <OrderForm
                  form={calc.form}
                  setField={calc.setField}
                  setConsent={calc.setConsent}
                  errors={calc.errors}
                  submitting={calc.submitting}
                  done={calc.done}
                  onSubmit={calc.submitOrder}
                  onReset={calc.resetDone}
                  onToken={calc.setToken}
                  website={calc.website}
                  setWebsite={calc.setWebsite}
                />
              </div>

              <div className="lg:col-span-6">
                <QuotePanel pricing={pricing} screenMeta={calc.screenMeta} qty={calc.qty} quote={calc.quote} priceActive={calc.priceActive} />
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
